import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { EditServiceAreaForm } from "./edit-service-area-form";
import { DialogTitle } from "@radix-ui/react-dialog";
import { useGetServiceAreas, type ServiceArea } from "@/api/service-areas";

export function ServiceAreasTable() {
  const [editingArea, setEditingArea] = useState<ServiceArea | null>(null);

  const {
    data: serviceAreas,
    isLoading,
    isError,
    refetch,
  } = useGetServiceAreas();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return (
      <div role="alert" className="space-y-2">
        <p>Unable to load service areas. Please try again.</p>
        <Button variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service Area</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {serviceAreas?.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="py-8 text-center text-muted-foreground"
                >
                  <p>No service areas found</p>
                  <p className="text-sm">
                    Create a service area to define your delivery coverage.
                  </p>
                </TableCell>
              </TableRow>
            )}
            {serviceAreas?.map((area) => (
              <TableRow key={area.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{area.name}</p>
                    <p className="text-sm text-gray-500">ID: {area.id}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="secondary"
                    className={`${
                      area.isActive ? "bg-green-500" : "bg-gray-500"
                    } text-white`}
                  >
                    {area.isActive ? "ACTIVE" : "INACTIVE"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {area.code}
                  </code>
                </TableCell>
                <TableCell>
                  <p className="text-sm">
                    {new Date(area.createdAt).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(area.createdAt).toLocaleTimeString()}
                  </p>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingArea(area)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editingArea} onOpenChange={() => setEditingArea(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Service Area</DialogTitle>
          </DialogHeader>
          {editingArea && (
            <EditServiceAreaForm
              serviceArea={editingArea}
              onClose={() => setEditingArea(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
